<template>
  <div class="app-shell">
    <Transition name="app-splash">
      <AppSplash v-if="showAppSplash" />
    </Transition>

    <!-- Global Top Right Actions (Orders + Language Switcher) -->
    <TopRightActions />
    <AppToast />
    <AppDialog />

    <div class="app-shell-content">
      <router-view v-slot="{ Component, route }">
        <keep-alive>
          <component
            v-if="route.meta && route.meta.keepAlive"
            :is="Component"
            :key="route.fullPath"
          />
        </keep-alive>
        <component
          v-if="!route.meta || !route.meta.keepAlive"
          :is="Component"
          :key="route.fullPath"
        />
      </router-view>
    </div>

    <div
      v-if="showGlobalCampaignPoster"
      class="campaign-global-mask fixed inset-0 z-[9999] flex items-center justify-center bg-black/25 backdrop-blur-[1px] p-3 sm:p-4"
      @click.self="closeGlobalCampaignPoster"
    >
      <div class="w-full max-w-[380px] max-h-[86vh] rounded-2xl bg-white/95 dark:bg-gray-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.28)] overflow-hidden border border-white/60 dark:border-gray-700/60 flex flex-col">
        <div class="px-4 py-3 bg-gradient-to-r from-violet-50/70 to-white/90 backdrop-blur flex items-start justify-between border-b border-gray-100 dark:border-gray-800/80">
          <div class="min-w-0 pr-3">
            <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ t('Campaign_Poster_Title') }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-300">{{ t('Campaign_Poster_Subtitle') }}</p>
          </div>
          <button
            type="button"
            class="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800/90 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors text-base leading-none"
            aria-label="Close campaign poster"
            @click="closeGlobalCampaignPoster"
          >
            ×
          </button>
        </div>

        <div class="campaign-global-poster-scroll p-3 bg-gray-50 dark:bg-gray-900/80 flex-1 overflow-y-auto">
          <img
            :src="globalCampaignPosterSrc"
            alt="campaign poster"
            class="block mx-auto w-full h-auto rounded-xl shadow-sm bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, inject, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { setStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import TopRightActions from '@/components/TopRightActions.vue'
import AppToast from '@/components/AppToast.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppSplash from '@/components/AppSplash.vue'
import logger from '@/utils/logger'
import { useI18n } from 'vue-i18n'
import { syncTheme } from '@/utils/theme'
import { hydrateLanguageFromTelegramCloud } from '@/utils/language'
import { showAppAlert, showAppConfirm } from '@/utils/telegramDialog'
import { bindTelegramEvent, unbindTelegramEvent } from '@/utils/telegramWebAppClient'
import { syncTelegramBackButton } from '@/utils/telegramBackButton'
import { initTelegramViewport, destroyTelegramViewport, shouldExpandTelegramViewport, updateTelegramHeaderColor } from '@/utils/telegramViewport'
import { initTelegramWebViewLayout, destroyTelegramWebViewLayout } from '@/utils/tgWebViewLayout'
import { getPendingPayment, removePendingPayment } from '@/utils/pendingPaymentStorage'
import { claimPaymentResultDialog } from '@/utils/paymentResultGuard'
import { loadTelegramStarsConfig } from '@/utils/telegramStarsPayment'

const api = inject('api')
const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()
const isTelegramWebAppAvailable = ref(true)
const isCheckingPendingPayment = ref(false)
const showGlobalCampaignPoster = ref(false)
const globalCampaignPosterSrc = ref('/campaign/marketing-email/en-poster.png')
const BODY_CLASS_CAMPAIGN_DIALOG_OPEN = 'campaign-dialog-open'
const BODY_CLASS_APP_SPLASH_OPEN = 'app-splash-open'
const hasShownAppreciationPosterOnce = ref(false)
const showAppSplash = ref(true)
const SPLASH_MIN_MS = 650
const SPLASH_MAX_MS = 3200
let splashStartedAt = 0

const isAppreciationCampaign = () => {
  return String(route.query.campaign || '').trim().toLowerCase() === 'appreciation-week'
}

const getGlobalCampaignPosterSrc = () => {
  const langQuery = String(route.query.lang || '').toLowerCase()
  const localeLang = String(locale?.value || '').toLowerCase()
  const shouldUseZh = langQuery.startsWith('zh') || localeLang.startsWith('zh')
  return shouldUseZh ? '/campaign/marketing-email/zh-poster.png' : '/campaign/marketing-email/en-poster.png'
}

const updateGlobalCampaignPoster = () => {
  if (!isAppreciationCampaign()) {
    showGlobalCampaignPoster.value = false
    return
  }
  if (hasShownAppreciationPosterOnce.value) {
    showGlobalCampaignPoster.value = false
    return
  }
  globalCampaignPosterSrc.value = getGlobalCampaignPosterSrc()
  showGlobalCampaignPoster.value = true
  hasShownAppreciationPosterOnce.value = true
}

const closeGlobalCampaignPoster = () => {
  showGlobalCampaignPoster.value = false
}

const initTelegramApp = async () => {
  const isTgEnv = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData
  const forceTg = process.env.VUE_APP_FORCE_TG_ONLY === 'true'

  // 1. Check if running in Telegram WebApp
  if (!isTgEnv) {
    if (forceTg) {
      isTelegramWebAppAvailable.value = false
      logger.warn('Not running in Telegram WebApp environment')
    } else {
      logger.debug('Running outside Telegram (Force TG mode is OFF)')
    }
    return
  }

  document.body.classList.add('tg-webapp')

  // 2. Initialize Telegram WebApp
  const tg = window.Telegram.WebApp
  tg.ready()
  if (shouldExpandTelegramViewport(tg)) {
    tg.expand()
  }
  syncTheme()
  initTelegramViewport(tg)
  await hydrateLanguageFromTelegramCloud(locale)
  syncTelegramBackButton(route, router)

  // 3. Auto Login using initData
  await loginWithTelegram(tg.initData)
  await loadTelegramStarsConfig(api)
}

const loginWithTelegram = async (initData) => {
  try {
    const response = await api.auth.telegramLogin(initData)
    const finalData = response.data && response.data.data
    if (finalData && finalData.token) {
      // Store JWT token
      setStorage(STORAGE_KEYS.TOKEN, finalData.token)
      // Store user info if returned
      if (finalData.user) {
        setStorage(STORAGE_KEYS.TELEGRAM_USER_ID, finalData.user.id)
      }
    }
  } catch (error) {
    // Error is already handled by API interceptor
    // Optional: Allow usage without login or show error
    // For now, we assume public access is okay or handled by specific views
  }
}

/**
 * 检测是否有待支付订单，处理用户从外部支付页返回的场景
 */
const checkPendingPayment = async () => {
  // 如果正在支付状态页，交给该页面处理
  if (route.path === '/payment-status') {
    return
  }
  
  // 防止重复检查
  if (isCheckingPendingPayment.value) return
  isCheckingPendingPayment.value = true

  const pendingPayment = await getPendingPayment()
  if (!pendingPayment) {
    isCheckingPendingPayment.value = false
    return
  }

  if (!pendingPayment?.orderNo) {
    await removePendingPayment()
    isCheckingPendingPayment.value = false
    return
  }

  // 检查是否在下单后 30 分钟内，超时则不再提示
  const createdAt = pendingPayment.createdAt || 0
  const thirtyMinutes = 30 * 60 * 1000
  if (Date.now() - createdAt > thirtyMinutes) {
    logger.debug('Pending payment expired (over 30 minutes), removing')
    await removePendingPayment()
    isCheckingPendingPayment.value = false
    return
  }

  // 使用订单状态接口，直接查数据库，更可靠
  // StatusCode: 0=未支付, 1=已支付, 2=已过期, 3=已开卡
  try {
    const res = await api.order.getOrderStatus(pendingPayment.orderNo)
    const data = res?.data?.data
    const statusCode = data?.StatusCode
    
    if (statusCode === undefined || statusCode === null) {
      // 状态未知，询问用户是否继续支付
      showContinuePaymentDialog(pendingPayment)
      isCheckingPendingPayment.value = false
      return
    }
    
    // 1 = 已支付, 3 = 已开卡（都算成功）
    if (statusCode === 1 || statusCode === 3) {
      await removePendingPayment()
      if (!claimPaymentResultDialog(pendingPayment.orderNo, 'success')) {
        router.push('/orders')
        return
      }
      await showAppAlert(
        t('Payment_Success_Message'),
        t('Payment_Success_Title'),
        {
          confirmButtonText: t('Payment_View_Order'),
          type: 'success',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false,
        },
      )
      router.push('/orders')
    } else if (statusCode === 2) {
      // 2 = 已过期，清除记录
      await removePendingPayment()
    } else if (statusCode === 0) {
      // 0 = 未支付，询问用户是否继续
      showContinuePaymentDialog(pendingPayment)
    }
  } catch (error) {
    logger.error('Failed to check order status:', error)
    // 查询失败，询问用户是否继续支付
    showContinuePaymentDialog(pendingPayment)
  } finally {
    isCheckingPendingPayment.value = false
  }
}

/**
 * 显示"是否继续支付"对话框
 */
const showContinuePaymentDialog = async (pendingPayment) => {
  const shouldContinue = await showAppConfirm(
    t('Payment_Pending_Message', { orderNo: pendingPayment.orderNo, amount: pendingPayment.amountUsd }),
    t('Payment_Pending_Title'),
    {
      confirmButtonText: t('Payment_Continue'),
      cancelButtonText: t('Payment_Cancel'),
      type: 'info',
    },
  )

  if (shouldContinue) {
    router.push('/payment-status')
    return
  }

  await removePendingPayment()
}

async function initializeApp() {
  await Promise.race([
    Promise.all([
      getProductList(),
      getSimProductList(),
    ]),
    new Promise((resolve) => setTimeout(resolve, SPLASH_MAX_MS)),
  ])

  // 延迟检测待支付订单，确保 API 和路由已就绪
  setTimeout(() => {
    checkPendingPayment()
  }, 500)
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const hideAppSplash = async () => {
  const elapsed = Date.now() - splashStartedAt
  if (elapsed < SPLASH_MIN_MS) {
    await wait(SPLASH_MIN_MS - elapsed)
  }
  showAppSplash.value = false
}

async function bootstrapApp() {
  splashStartedAt = Date.now()

  try {
    await Promise.race([
      Promise.all([
        initTelegramApp(),
        initializeApp(),
      ]),
      wait(SPLASH_MAX_MS),
    ])
  } finally {
    await hideAppSplash()
  }
}

let appFrameResizeObserver = null
let tgLayoutSyncHandler = null
let tgAppFrameSyncHandler = null
let tgActivatedHandler = null

const syncAppFrame = () => {
  if (!document.getElementById('app')) return
  const root = document.documentElement
  const isTg = !!(window.Telegram?.WebApp?.initData)

  if (isTg) {
    document.body.classList.add('tg-webapp')
    root.style.setProperty('--app-left', '0px')
    root.style.setProperty('--app-width', '100%')
    root.style.setProperty('--app-right', '0px')
  } else {
    root.style.removeProperty('--app-left')
    root.style.removeProperty('--app-width')
    root.style.removeProperty('--app-right')
    document.body.classList.remove('tg-webapp')
  }
}

onMounted(() => {
  bootstrapApp()
  syncAppFrame()
  initTelegramWebViewLayout()
  window.addEventListener('resize', syncAppFrame)
  tgAppFrameSyncHandler = () => syncAppFrame()
  window.addEventListener('tg-app-frame-sync', tgAppFrameSyncHandler)
  const tg = window.Telegram?.WebApp
  if (tg?.onEvent) {
    tgLayoutSyncHandler = () => syncAppFrame()
    tg.onEvent('fullscreenChanged', tgLayoutSyncHandler)
    tg.onEvent('viewportChanged', tgLayoutSyncHandler)

    tgActivatedHandler = () => {
      checkPendingPayment()
      getProductList()
      getSimProductList()
    }
    bindTelegramEvent('activated', tgActivatedHandler)
  }
  const appEl = document.getElementById('app')
  if (appEl && typeof ResizeObserver !== 'undefined') {
    appFrameResizeObserver = new ResizeObserver(() => syncAppFrame())
    appFrameResizeObserver.observe(appEl)
  }
})

onBeforeUnmount(() => {
  destroyTelegramViewport()
  destroyTelegramWebViewLayout()
  window.removeEventListener('resize', syncAppFrame)
  if (tgAppFrameSyncHandler) {
    window.removeEventListener('tg-app-frame-sync', tgAppFrameSyncHandler)
    tgAppFrameSyncHandler = null
  }
  const tg = window.Telegram?.WebApp
  if (tg?.offEvent && tgLayoutSyncHandler) {
    tg.offEvent('fullscreenChanged', tgLayoutSyncHandler)
    tg.offEvent('viewportChanged', tgLayoutSyncHandler)
    tgLayoutSyncHandler = null
  }
  if (tgActivatedHandler) {
    unbindTelegramEvent('activated', tgActivatedHandler)
    tgActivatedHandler = null
  }
  if (appFrameResizeObserver) {
    appFrameResizeObserver.disconnect()
    appFrameResizeObserver = null
  }
  document.body.classList.remove(BODY_CLASS_CAMPAIGN_DIALOG_OPEN)
  document.body.classList.remove(BODY_CLASS_APP_SPLASH_OPEN)
})

watch(
  () => route.fullPath,
  () => {
    updateGlobalCampaignPoster()
    updateTelegramHeaderColor()
  },
  { immediate: true }
)

watch(showGlobalCampaignPoster, (visible) => {
  if (visible) {
    document.body.classList.add(BODY_CLASS_CAMPAIGN_DIALOG_OPEN)
    return
  }
  document.body.classList.remove(BODY_CLASS_CAMPAIGN_DIALOG_OPEN)
})

watch(showAppSplash, (visible) => {
  document.body.classList.toggle(BODY_CLASS_APP_SPLASH_OPEN, visible)
}, { immediate: true })

// 获取 eSIM 产品列表（不带电话号码）
const getProductList = async () => {
  try {
    const response = await api.product.queryEsimProductList()
    setStorage(STORAGE_KEYS.PRODUCT_LIST, response.data.data[0])
  } catch (err) {
    logger.error('Failed to load product list:', err)
  }
}

// 获取 SIM 产品列表（带电话号码）
const getSimProductList = async () => {
  try {
    const response = await api.product.querySimProductList()
    setStorage(STORAGE_KEYS.SIM_PRODUCT_LIST, response.data.data[0])
  } catch (err) {
    logger.error('Failed to load SIM product list:', err)
  }
}
</script>


<style>
nav {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}
/* 訂單頁「查看卡密」彈窗打開時隱藏右上角語言選擇器，避免遮擋 */
body.esim-dialog-open .top-right-actions {
  visibility: hidden;
}

/* Campaign 海报弹窗打开时，隐藏右上角浮层并锁定背景滚动 */
body.campaign-dialog-open .top-right-actions {
  visibility: hidden;
}

body.campaign-dialog-open {
  overflow: hidden;
}

.campaign-global-poster-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.6) transparent;
}

.campaign-global-poster-scroll::-webkit-scrollbar {
  width: 6px;
}

.campaign-global-poster-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.campaign-global-poster-scroll::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.55);
  border-radius: 9999px;
}

.campaign-global-poster-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.75);
}

@supports (-webkit-touch-callout: none) {
  .campaign-global-poster-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}

iframe[src*="salesmartly" i],
[id*="salesmartly" i],
[class*="salesmartly" i] {
  display: none !important;
  visibility: hidden !important;
}

body.app-splash-open {
  overflow: hidden;
}

body.app-splash-open .top-right-actions {
  visibility: hidden;
}

.app-splash-enter-active,
.app-splash-leave-active {
  transition: opacity 0.32s ease;
}

.app-splash-enter-from,
.app-splash-leave-to {
  opacity: 0;
}
</style>
